import type React from 'react';
import { useEffect, useId, useState } from 'react';
import { TagSelect, type SelectOption } from '@skupreme/shared/ui';
import { Controller, type Control } from 'react-hook-form';
import { CommonUtility } from '@utility/common';
import type { FormHelpToolTipProps } from 'types/common/form-fields';
import { CustomField, FieldLabel, FieldMessage } from './form-field/chrome';
import type { IEmailData } from 'types/common/email';

interface IMultipleEmailFieldProps {
  error?: boolean;
  text?: string;
  control: Control<any>;
  name: string;
  /** The first this many addresses cannot be removed. */
  defaultEmails?: number;
  required?: boolean;
  disabled?: boolean;
  emails?: IEmailData[];
  hint?: string;
  loading?: boolean;
  minwidth?: number;
  defaultValue?: string[];
  className?: string;
  placeholder?: string;
  helpText?: string;
  helpPosition?: FormHelpToolTipProps['helpPosition'];
  existingEmails?: string[];
}

const EMAIL_PATTERN =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const validateEmail = (email: string) =>
  EMAIL_PATTERN.test(String(email).toLowerCase());

function toOption(contact: IEmailData): SelectOption {
  const email = contact.email ?? '';
  const fullName = [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(' ');
  return fullName
    ? { value: email, label: fullName, description: email }
    : { value: email, label: email };
}

const matchesNameOrEmail = (option: SelectOption, query: string) => {
  const needle = query.trim().toLowerCase();
  return (
    option.label.toLowerCase().includes(needle) ||
    option.value.toLowerCase().includes(needle)
  );
};

const MultipleEmailField: React.FC<IMultipleEmailFieldProps> = ({
  error,
  text,
  control,
  name,
  defaultEmails = 0,
  required,
  disabled,
  emails,
  hint,
  loading,
  minwidth,
  defaultValue,
  className,
  placeholder,
  helpText,
  helpPosition = 'top center',
  existingEmails = [],
}) => {
  const labelId = useId();
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [optionError, setOptionError] = useState<string | null>(null);

  useEffect(() => {
    if (CommonUtility.isValidArray(emails)) {
      setOptions(
        (emails || []).filter(contact => !!contact.email).map(toOption),
      );
    }
  }, [emails]);

  const checkNotExists = (email: string) =>
    email && CommonUtility.isValidArray(existingEmails)
      ? !existingEmails.includes(email)
      : true;

  const handleChange = (
    next: string[],
    current: string[],
    onChange: (value: string[]) => void,
  ) => {
    setOptionError(null);
    if (next.length < defaultEmails) {
      return;
    }
    const added = next.find(email => !current.includes(email));
    if (added !== undefined) {
      if (!validateEmail(added)) {
        setOptionError(`${added} is not a valid e-mail`);
        return;
      }
      if (!checkNotExists(added)) {
        setOptionError(`${added} is already added`);
        return;
      }
      if (!options.some(option => option.value === added)) {
        setOptions(prev => [...prev, { value: added, label: added }]);
      }
    }
    onChange(next);
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, onBlur, value } }) => {
        const current: string[] = Array.isArray(value) ? value : [];
        return (
          <CustomField
            style={minwidth ? { minWidth: minwidth } : undefined}
            className={className}
          >
            <FieldLabel
              id={labelId}
              label={text}
              required={required}
              helpText={helpText}
              helpPosition={helpPosition}
            />
            <div className="sk:flex sk:w-full sk:[&>*]:min-w-0 sk:[&>*]:flex-1">
              <TagSelect
                {...(text
                  ? { 'aria-labelledby': labelId }
                  : { 'aria-label': placeholder || 'Email' })}
                options={options}
                values={current}
                onChange={next => handleChange(next, current, onChange)}
                onInputChange={() => setOptionError(null)}
                onBlur={onBlur}
                filter={matchesNameOrEmail}
                allowsCustomValue
                placeholder={placeholder || text || 'Email'}
                emptyMessage="Type an e-mail and press Enter"
                isLoading={loading}
                isDisabled={disabled}
                isInvalid={error}
                isRequired={required}
                className="sk:w-full"
              />
            </div>
            {(hint || optionError) && (
              <FieldMessage>{hint || optionError}</FieldMessage>
            )}
          </CustomField>
        );
      }}
    />
  );
};

export default MultipleEmailField;
